/**
 * F45 (Wave 2D) — conversion funnels page.
 *
 * Route: `/analytics/funnels`. Permission: `analytics:read`.
 *
 * Funnel Select (booking / open_game / tournament / trainer_booking) + sport/city
 * segment Selects + DateRangePicker + compare Switch → Chart funnel + per-step
 * Stat cards (count + conversion + drop-off + PoP) via the shared FunnelPanel.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  DateRangePicker,
  Select,
  Skeleton,
  Switch,
  type DateRange,
  type SelectOption,
} from '@bravemove/ui-web';
import {
  ANALYTICS_FUNNELS,
  type AnalyticsFunnelKey,
  type AnalyticsFunnelResult,
} from '@bravemove/types';
import { adminAnalyticsApi } from '../../api/analytics';
import { PageHeader } from '../../components/PageHeader';
import { AnalyticsNav } from './AnalyticsNav';
import { FunnelPanel } from './components/FunnelPanel';
import { defaultAnalyticsRange, type AnalyticsRangeValue } from './analyticsHelpers';

type SegmentDim = 'all' | 'sport' | 'city';

export function AnalyticsFunnelsPage() {
  const { t, locale } = useI18n();

  const [funnel, setFunnel] = useState<AnalyticsFunnelKey>('booking');
  const [range, setRange] = useState<AnalyticsRangeValue>(() => defaultAnalyticsRange());
  const [compare, setCompare] = useState(false);
  const [segmentDim, setSegmentDim] = useState<SegmentDim>('all');
  const [segmentVal, setSegmentVal] = useState('');

  const [result, setResult] = useState<AnalyticsFunnelResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(
        await adminAnalyticsApi.getFunnel({
          funnel,
          from: range.from,
          to: range.to,
          compare,
          segmentDim,
          ...(segmentDim !== 'all' && segmentVal ? { segmentVal } : {}),
        }),
      );
    } catch {
      setError(t('admin:analytics.error'));
    } finally {
      setLoading(false);
    }
  }, [funnel, range.from, range.to, compare, segmentDim, segmentVal, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchFunnel();
  }, [fetchFunnel]);

  const funnelOptions = useMemo<readonly SelectOption[]>(
    () => ANALYTICS_FUNNELS.map((f) => ({ value: f, label: t(`admin:analytics.funnel.${f}`) })),
    [t],
  );
  const segmentOptions = useMemo<readonly SelectOption[]>(
    () => [
      { value: 'all', label: t('admin:analytics.funnel.segmentAll') },
      { value: 'sport', label: t('admin:analytics.funnel.segmentSport') },
      { value: 'city', label: t('admin:analytics.funnel.segmentCity') },
    ],
    [t],
  );

  const onCustomRange = (dr: DateRange) =>
    setRange((prev) => ({ from: dr.start ?? prev.from, to: dr.end ?? prev.to }));

  return (
    <div className="space-y-6" data-testid="analytics-funnels-page">
      <PageHeader
        eyebrow="Analytics"
        title={t('admin:analytics.funnel.title')}
        subtitle={t('admin:analytics.funnel.subtitle')}
      />
      <AnalyticsNav />

      <div className="flex flex-wrap items-end gap-3" data-testid="analytics-funnel-controls">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-ink-3">
            {t('admin:analytics.funnel.selectFunnel')}
          </span>
          <Select
            options={funnelOptions}
            value={funnel}
            onChange={(e) => setFunnel(e.target.value as AnalyticsFunnelKey)}
            data-testid="analytics-funnel-select"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-ink-3">
            {t('admin:analytics.funnel.segmentDim')}
          </span>
          <Select
            options={segmentOptions}
            value={segmentDim}
            onChange={(e) => setSegmentDim(e.target.value as SegmentDim)}
            data-testid="analytics-funnel-segment-dim"
          />
        </label>
        {segmentDim !== 'all' ? (
          <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wider text-ink-3">
              {t('admin:analytics.funnel.segmentVal')}
            </span>
            <input
              className="rounded-lg border border-hairline bg-surface px-3 py-2 text-[13px] text-ink"
              value={segmentVal}
              onChange={(e) => setSegmentVal(e.target.value)}
              data-testid="analytics-funnel-segment-val"
            />
          </label>
        ) : null}
        <Switch
          label={t('admin:analytics.period.compareToPrevious')}
          checked={compare}
          onChange={(e) => setCompare(e.target.checked)}
          data-testid="analytics-funnel-compare"
        />
      </div>

      <DateRangePicker
        value={{ start: range.from, end: range.to }}
        onChange={onCustomRange}
        locale={locale}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading || !result ? (
        <Skeleton className="h-72 w-full rounded-xl" data-testid="analytics-funnel-loading" />
      ) : (
        <section className="rounded-xl border border-hairline bg-surface p-4">
          <FunnelPanel result={result} />
        </section>
      )}
    </div>
  );
}

export default AnalyticsFunnelsPage;
